/**
 * Public Projects Page
 * Displays construction project portfolio with category filtering
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, Image as ImageIcon, FileText, ArrowRight } from 'lucide-react';
import { projectsService } from '@/services/projectsService';
import { queryKeys, STALE_TIMES } from '@/lib/query-config';
import type { Project, ProjectCategory } from '@/types/projects';

const Projects: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<ProjectCategory | 'all'>('all');

  const { data: projects, isLoading, error } = useQuery({
    queryKey: queryKeys.projects.list({ category: selectedCategory === 'all' ? undefined : selectedCategory }),
    queryFn: async () => {
      const result = await projectsService.getProjects({
        category: selectedCategory === 'all' ? undefined : selectedCategory,
        isAdmin: false,
      });
      if (!result.success) {
        throw result.error;
      }
      return result.data;
    },
    staleTime: STALE_TIMES.projects,
  });

  const categories: Array<{ value: ProjectCategory | 'all'; label: string }> = [
    { value: 'all', label: 'All Projects' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'residential', label: 'Residential' },
    { value: 'hurricane_rehab', label: 'Hurricane Rehab' },
  ];

  const getCategoryBadgeColor = (category: ProjectCategory): string => {
    switch (category) {
      case 'commercial':
        return 'bg-blue-500 hover:bg-blue-600';
      case 'residential':
        return 'bg-green-500 hover:bg-green-600';
      case 'hurricane_rehab':
        return 'bg-orange-500 hover:bg-orange-600';
      default:
        return 'bg-gray-500';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-warm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading projects...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-warm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground mb-4">Error Loading Projects</h2>
            <p className="text-muted-foreground">Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  const displayedProjects = projects || [];

  return (
    <div className="min-h-screen bg-gradient-warm">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 via-heritage/5 to-transparent">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Our <span className="text-heritage">Projects</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Explore our portfolio of completed construction projects across commercial, residential, and hurricane rehabilitation services.
          </p>
          <Button size="lg" asChild className="group">
            <Link to="/form/quote-request">
              <FileText className="w-5 h-5 mr-2" />
              Request a Quote
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Category Filters */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-background/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <Button
                key={category.value}
                variant={selectedCategory === category.value ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category.value)}
                className="capitalize"
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {displayedProjects.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Projects Found</h3>
              <p className="text-muted-foreground">
                {selectedCategory === 'all'
                  ? 'No projects have been added yet.'
                  : `No ${categories.find(c => c.value === selectedCategory)?.label.toLowerCase()} projects found.`}
              </p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {displayedProjects.map((project) => (
                <Card key={project.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  {project.featured_image_url ? (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={project.featured_image_url}
                        alt={project.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-muted flex items-center justify-center">
                      <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
                    </div>
                  )}
                  
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-lg flex-1">{project.title}</CardTitle>
                      <Badge className={getCategoryBadgeColor(project.category)}>
                        {project.category === 'commercial' ? 'Commercial' :
                         project.category === 'residential' ? 'Residential' :
                         'Hurricane Rehab'}
                      </Badge>
                    </div>
                    {project.description && (
                      <CardDescription className="line-clamp-2 mt-2">
                        {project.description}
                      </CardDescription>
                    )}
                  </CardHeader>
                  
                  <CardContent>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      {project.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="line-clamp-1">{project.location}</span>
                        </div>
                      )}
                      {project.completion_date && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 flex-shrink-0" />
                          <span>
                            Completed: {new Date(project.completion_date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 to-heritage/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Get a free quote for your construction project. Our team will review your needs and provide a detailed estimate.
          </p>
          <Button size="lg" asChild className="group">
            <Link to="/form/quote-request">
              <FileText className="w-5 h-5 mr-2" />
              Request a Free Quote
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Projects;
